import { Link } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import { Copy, FileText, MoreVertical, Pencil, Star, Trash2 } from 'lucide-react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuthor } from '@/hooks/useAuthor';
import type { DocSummary } from '@/lib/doc/types';
import { formatRelativeTime, shortPubkey } from '@/lib/identity';
import { cn } from '@/lib/utils';

interface DocCardProps {
  doc: DocSummary;
  viewerPubkey?: string;
  isFavorite: boolean;
  onToggleFavorite: () => void;
  onRename: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

export function docPath(doc: Pick<DocSummary, 'ownerPubkey' | 'docId'>): string {
  return `/doc/${nip19.npubEncode(doc.ownerPubkey)}/${doc.docId}`;
}

export function DocCard({
  doc,
  viewerPubkey,
  isFavorite,
  onToggleFavorite,
  onRename,
  onDuplicate,
  onDelete,
}: DocCardProps) {
  const isOwner = viewerPubkey === doc.ownerPubkey;
  const owner = useAuthor(doc.ownerPubkey);
  const ownerName = owner.data?.metadata?.display_name
    || owner.data?.metadata?.name
    || shortPubkey(doc.ownerPubkey);

  return (
    <Card className="group relative overflow-hidden p-0 transition-all hover:shadow-md hover:border-border/80 focus-within:ring-2 focus-within:ring-ring/50">
      <Link
        to={docPath(doc)}
        className="block space-y-3 p-4 pb-12 outline-none"
        aria-label={`Open ${doc.title}`}
      >
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <FileText className="h-4.5 w-4.5" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="truncate text-sm font-semibold leading-5">{doc.title}</h3>
            <p className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
              {isOwner ? (
                <span className="truncate">You</span>
              ) : (
                <span className="truncate">Shared by {ownerName}</span>
              )}
              <span aria-hidden>·</span>
              <span className="shrink-0">{formatRelativeTime(doc.updatedAt)}</span>
            </p>
          </div>
        </div>

        {doc.collaboratorPubkeys.length > 0 && (
          <div className="flex items-center -space-x-1.5" aria-label={`${doc.collaboratorPubkeys.length} collaborators`}>
            {doc.collaboratorPubkeys.slice(0, 4).map((pubkey) => (
              <CollaboratorAvatar key={pubkey} pubkey={pubkey} />
            ))}
            {doc.collaboratorPubkeys.length > 4 && (
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-muted text-[10px] font-medium text-muted-foreground ring-2 ring-card">
                +{doc.collaboratorPubkeys.length - 4}
              </span>
            )}
            <span className="ml-3 text-xs text-muted-foreground">
              {doc.collaboratorPubkeys.length} {doc.collaboratorPubkeys.length === 1 ? 'collaborator' : 'collaborators'}
            </span>
          </div>
        )}
      </Link>

      {doc.role !== 'owner' && (
        <span className="absolute bottom-3 left-4 rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium capitalize text-muted-foreground">
          {doc.role}
        </span>
      )}

      <div className="absolute bottom-2.5 right-2 flex items-center gap-0.5">
        <Button
          variant="ghost"
          size="icon"
          className={cn('h-8 w-8', isFavorite ? 'text-amber-500 hover:text-amber-500' : 'text-muted-foreground/0 group-hover:text-muted-foreground focus-visible:text-muted-foreground')}
          onClick={(event) => {
            event.preventDefault();
            onToggleFavorite();
          }}
          aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
          aria-pressed={isFavorite}
        >
          <Star className={cn('h-4 w-4', isFavorite && 'fill-current')} />
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground/0 group-hover:text-muted-foreground focus-visible:text-muted-foreground data-[state=open]:text-muted-foreground"
              aria-label={`Actions for ${doc.title}`}
            >
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuItem asChild>
              <Link to={docPath(doc)}>Open</Link>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onDuplicate}>
              <Copy className="h-4 w-4" /> Duplicate
            </DropdownMenuItem>
            {isOwner && (
              <DropdownMenuItem onClick={onRename}>
                <Pencil className="h-4 w-4" /> Rename
              </DropdownMenuItem>
            )}
            {isOwner && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={onDelete}>
                  <Trash2 className="h-4 w-4" /> Delete
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </Card>
  );
}

function CollaboratorAvatar({ pubkey }: { pubkey: string }) {
  const author = useAuthor(pubkey);
  const name = author.data?.metadata?.display_name
    || author.data?.metadata?.name
    || shortPubkey(pubkey);
  return (
    <span title={name} className="ring-2 ring-card rounded-full">
      <Avatar className="h-6 w-6">
        {author.data?.metadata?.picture ? (
          <AvatarImage src={author.data.metadata.picture} alt={name} />
        ) : null}
        <AvatarFallback className="text-[10px]">{name.slice(0, 1).toUpperCase()}</AvatarFallback>
      </Avatar>
    </span>
  );
}
